<template>
  <div class="upgrade-setting">
    <GlobalInfoBar
      title="产品升级设置"
      subtitle="管理产品升级方案，配置起始折抵数、目标折抵数及方案售价"
    />

    <div class="flow-note">
      <strong>升级方案说明：</strong><br>
      每个机型可配置多个升级方案，用户在小程序激活时<strong>单选</strong>其中一个方案。每个方案包含：起始折抵数、目标折抵数、方案售价。<br>
      折抵金额 = 机型指导价 × 升级后折扣。用户仅需支付方案售价。
    </div>

    <GlobalChunk title="升级方案列表" :padding="[20, 20, 20, 20]">
      <template slot="header">
        <el-select
          v-model="statusFilter"
          clearable
          size="small"
          placeholder="全部状态"
          style="width: 120px; margin-right: 10px;"
          @change="handleStatusChange"
        >
          <el-option label="启用" value="1" />
          <el-option label="已停用" value="0" />
        </el-select>
        <el-button type="primary" size="small" @click="openModal()">+ 创建升级方案</el-button>
      </template>

      <GlobalTable
        ref="GlobalTable"
        :columns="tableColumns"
        :data="planList"
        :isIndex="false"
        :isPagination="true"
        :currentPage="curPage"
        :total="total"
        :pageSize="pageSize"
        @handleCurrentChange="currentChange"
      >
        <el-table-column slot="name" label="方案名称" align="center">
          <template slot-scope="{ row }">
            <strong>{{ row.name }}</strong><br>
            <span class="sub-text">{{ row.subtitle }}</span>
          </template>
        </el-table-column>
        <el-table-column slot="startDiscount" label="起始折抵数" align="center" width="120">
          <template slot-scope="{ row }">
            <span class="discount-text">{{ row.startDiscount }}折</span>
          </template>
        </el-table-column>
        <el-table-column slot="targetDiscount" label="目标折抵数" align="center" width="120">
          <template slot-scope="{ row }">
            <el-tag size="small">{{ row.targetDiscount }}折</el-tag>
          </template>
        </el-table-column>
        <el-table-column slot="price" label="方案售价" align="center" width="120">
          <template slot-scope="{ row }">
            <span class="price-text">¥{{ row.price }}</span>
          </template>
        </el-table-column>
        <el-table-column slot="costConfig" label="成本配置" align="center" width="110">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="openCostView(row)">查看</el-button>
          </template>
        </el-table-column>
        <el-table-column slot="status" label="状态" align="center" width="100">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 'active' ? 'success' : 'info'" size="small">
              {{ row.status === 'active' ? '启用' : '已停用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column slot="action" label="操作" align="center" width="170">
          <template slot-scope="{ row, $index }">
            <el-button type="warning" size="mini" @click="openModal(row)">编辑</el-button>
            <el-popconfirm title="确定要删除该升级方案吗？" @confirm="deletePlan($index)">
              <el-button slot="reference" type="danger" size="mini" style="margin-left:8px;">删除</el-button>
            </el-popconfirm>
          </template>
        </el-table-column>
      </GlobalTable>
    </GlobalChunk>

    <div class="annotation-box">
      <strong>与小程序联动说明：</strong>此处设置的升级方案将直接展示在小程序「设备信息+升级」页面，用户激活时可单选其中一个方案并支付加价费用。基础折扣（2折）为系统默认，升级方案在此基础上增加折扣。
    </div>

    <el-dialog
      :title="(editingPlan ? '编辑' : '创建') + '升级方案'"
      :visible.sync="modalVisible"
      width="750px"
      :close-on-click-modal="false"
    >
      <el-form :model="form" label-width="130px" size="small">
        <el-form-item label="方案名称" required>
          <el-input v-model="form.name" placeholder="如：升级方案 A" />
        </el-form-item>
        <el-form-item label="副标题" required>
          <el-input v-model="form.subtitle" placeholder="如：超值换新" />
          <div class="hint">展示在方案名称下方</div>
        </el-form-item>
        <div class="form-grid">
          <el-form-item label="起始折抵数（折）" required>
            <el-input-number v-model="form.startDiscount" :min="0.5" :max="10" :step="0.5" />
            <div class="hint">方案要求的当前折抵数</div>
          </el-form-item>
          <el-form-item label="目标折抵数（折）" required>
            <el-input-number v-model="form.targetDiscount" :min="0.5" :max="10" :step="0.5" />
            <div class="hint">升级后的折抵数</div>
          </el-form-item>
        </div>
        <el-form-item label="方案售价（元）" required>
          <el-input-number v-model="form.price" :min="1" :max="9999" />
          <div class="hint">产品方案的售价，用户选择此方案需支付的费用</div>
        </el-form-item>

        <el-form-item label="成本配置" required>
          <div class="cost-editor">
            <el-table :data="form.costConfig" border size="mini" style="width:100%;">
              <el-table-column label="指导价区间（元）" align="center">
                <template slot-scope="{ row }">
                  <div class="range-inputs">
                    <el-input-number v-model="row.min" :min="0" :controls="false" size="mini" />
                    <span>-</span>
                    <el-input-number v-model="row.max" :min="0" :controls="false" size="mini" />
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="差价成本（元）" align="center" width="180">
                <template slot-scope="{ row }">
                  <el-input-number v-model="row.cost" :min="0" :controls="false" size="mini" />
                </template>
              </el-table-column>
              <el-table-column label="操作" align="center" width="100">
                <template slot-scope="{ $index }">
                  <el-button type="danger" size="mini" @click="removeCostRow($index)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
            <div class="cost-footer">
              <el-button size="mini" @click="addCostRow">+ 添加区间</el-button>
            </div>
          </div>
        </el-form-item>

        <el-form-item label="状态开关">
          <el-switch
            v-model="form.enabled"
            active-color="#52c41a"
            inactive-color="#dcdfe6"
          />
          <span class="switch-tip">开启（仅控制方案是否在小程序端展示，小程序端不显示开关状态）</span>
        </el-form-item>
      </el-form>

      <div class="preview-box">
        <div class="preview-title">方案预览（小程序展示效果）</div>
        <div class="preview-content">
          <strong>{{ form.name || '升级方案名称' }} · {{ form.subtitle || '副标题' }}</strong><br>
          {{ form.startDiscount || 0 }}折 -> {{ form.targetDiscount || 0 }}折<br>
          原折抵金额：¥{{ originalDeduction }}<br>
          <span class="price-text">折抵金额：¥{{ targetDeduction }}</span> · 方案售价
          <span class="price-text">¥{{ form.price || 0 }}</span>
        </div>
      </div>

      <span slot="footer">
        <el-button size="small" @click="modalVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="confirmSave">
          {{ editingPlan ? '保存修改' : '确认创建' }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="成本配置查看"
      :visible.sync="costVisible"
      width="520px"
      :close-on-click-modal="false"
    >
      <GlobalTable
        :columns="costColumns"
        :data="currentCostConfig"
        :isIndex="false"
        :isPagination="false"
      >
        <el-table-column slot="range" label="指导价区间（元）" align="center">
          <template slot-scope="{ row }">
            {{ formatRange(row) }}
          </template>
        </el-table-column>
        <el-table-column slot="cost" label="差价成本（元）" align="center">
          <template slot-scope="{ row }">
            ¥{{ row.cost }}
          </template>
        </el-table-column>
      </GlobalTable>
      <span slot="footer">
        <el-button size="small" @click="costVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import _api from '@/utils/request'

export default {
  name: '升级设置',
  data() {
    return {
      modalVisible: false,
      costVisible: false,
      editingPlan: null,
      form: this.getDefaultForm(),
      currentCostConfig: [],
      tableColumns: [
        { slotName: 'name' },
        { slotName: 'startDiscount' },
        { slotName: 'targetDiscount' },
        { slotName: 'price' },
        { slotName: 'costConfig' },
        { slotName: 'status' },
        { slotName: 'action' }
      ],
      costColumns: [
        { slotName: 'range' },
        { slotName: 'cost' }
      ],
      planList: [],
      curPage: 1,
      total: 0,
      pageSize: 10,
      statusFilter: ''
    }
  },
  computed: {
    originalDeduction() {
      return this.calcDeduction(this.form.startDiscount)
    },
    targetDeduction() {
      return this.calcDeduction(this.form.targetDiscount)
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    getDefaultForm() {
      return {
        name: '',
        subtitle: '',
        startDiscount: undefined,
        targetDiscount: 3,
        price: undefined,
        status: 'active',
        enabled: true,
        costConfig: []
      }
    },
    normalizePlan(plan) {
      const status = plan.status === 'disabled' || String(plan.status) === '0' ? 'disabled' : 'active'
      return {
        ...plan,
        id: plan.id || plan.plan_id,
        name: plan.name || plan.plan_name || '',
        subtitle: plan.subtitle || plan.label || '',
        startDiscount: Number(plan.startDiscount || plan.start_discount || 2),
        targetDiscount: Number(plan.targetDiscount || plan.target_discount || plan.discountTotal || 3),
        price: Number(plan.price || plan.plan_price || 0),
        status,
        enabled: status === 'active',
        costConfig: this.normalizeCostConfig(plan.costConfig || plan.cost_config || plan.cost_bands)
      }
    },
    normalizeCostConfig(list) {
      if (!Array.isArray(list) || list.length === 0) return []
      return list.map(item => ({
        ...item,
        min: Number(item.min !== undefined ? item.min : item.price_min),
        max: item.max !== undefined
          ? item.max
          : (item.price_max === null || item.price_max === '' || item.price_max === undefined ? null : Number(item.price_max)),
        cost: Number(item.cost !== undefined ? item.cost : item.cost_price)
      }))
    },
    buildSavePayload() {
      const payload = {
        plan_name: this.form.name,
        subtitle: this.form.subtitle,
        start_discount: String(this.form.startDiscount),
        target_discount: String(this.form.targetDiscount),
        plan_price: String(this.form.price),
        cost_bands: this.form.costConfig.map(item => ({
          price_min: String(item.min || 0),
          price_max: String(item.max === null || item.max === undefined ? '' : item.max),
          cost_price: String(item.cost || 0)
        }))
      }
      if (this.editingPlan && this.editingPlan.id) {
        payload.plan_id = String(this.editingPlan.id)
      }
      return payload
    },
    buildLocalPayload() {
      return {
        ...this.form,
        id: this.editingPlan && this.editingPlan.id,
        status: this.form.enabled ? 'active' : 'disabled'
      }
    },
    fetchList() {
      const params = {
        page: this.curPage,
        limit: this.pageSize,
        status: this.statusFilter
      }
      _api.upgradePlanList(params).then(res => {
        if (res) {
          const list = Array.isArray(res.data) ? res.data : ((res.data && res.data.list) || [])
          this.planList = list.map(this.normalizePlan)
          this.total = Number(res.count || (res.data && res.data.count) || 0)
        }
      }).catch(() => {
        this.planList = []
        this.total = this.planList.length
      })
    },
    handleStatusChange() {
      this.curPage = 1
      this.fetchList()
    },
    currentChange(page) {
      this.curPage = page
      this.fetchList()
    },
    openModal(plan) {
      this.editingPlan = plan || null
      if (plan) {
        this.form = {
          ...this.normalizePlan(plan),
          costConfig: this.cloneCostConfig(plan.costConfig || [])
        }
      } else {
        this.form = this.getDefaultForm()
      }
      this.modalVisible = true
    },
    openCostView(plan) {
      this.currentCostConfig = this.cloneCostConfig(plan.costConfig || [])
      this.costVisible = true
    },
    cloneCostConfig(list) {
      return list.map(item => ({ ...item }))
    },
    addCostRow() {
      this.form.costConfig.push({ min: 0, max: 1000, cost: 0 })
    },
    removeCostRow(index) {
      if (this.form.costConfig.length <= 1) {
        this.$message({ type: 'warning', message: '至少保留一个成本区间' })
        return
      }
      this.form.costConfig.splice(index, 1)
    },
    formatRange(row) {
      if (row.max === null || row.max === '' || row.max === undefined) {
        return `${row.min} 以上`
      }
      return `${row.min} - ${row.max}`
    },
    calcDeduction(discount) {
      const guidePrice = 6999
      const value = guidePrice * Number(discount || 0) / 10
      return value.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    deletePlan(i) {
      const plan = this.planList[i]
      _api.deleteUpgradePlan({ ids: String(plan.id) }).then(res => {
        this.$message({ type: 'success', message: (res && res.msg) || '方案已删除' })
        this.fetchList()
      }).catch(() => {
        this.$message({ type: 'success', message: '方案已删除（模拟）' })
        this.planList.splice(i, 1)
      })
    },
    confirmSave() {
      const payload = this.buildSavePayload()
      _api.upgradePlanSave(payload).then(res => {
        if(res.code == 1){
          const planId = (res && res.data && res.data.plan_id) || payload.plan_id
          const originEnabled = this.editingPlan ? this.editingPlan.enabled : true
          const needToggle = planId && originEnabled !== this.form.enabled
          const afterSave = needToggle
            ? _api.upgradePlanToggle({
              plan_id: String(planId),
              status: this.form.enabled ? '1' : '0'
            })
            : Promise.resolve()
          return afterSave
        }
      }).then(() => {
        this.$message({ type: 'success', message: this.editingPlan ? '升级方案修改成功' : '升级方案创建成功' })
        this.modalVisible = false
        this.fetchList()
      }).catch(() => {
        this.saveMock(this.buildLocalPayload())
      })
    },
    saveMock(payload) {
      if (this.editingPlan) {
        const index = this.planList.findIndex(item => item === this.editingPlan || item.name === this.editingPlan.name)
        if (index > -1) this.$set(this.planList, index, { ...payload })
      } else {
        this.planList.push({ ...payload })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.upgrade-setting {
  .flow-note {
    background: #fffbe6;
    border: 1px solid #ffe58f;
    border-radius: 4px;
    padding: 12px 16px;
    margin: 12px 0;
    font-size: 13px;
    color: #595959;
    line-height: 1.8;

    strong {
      color: #262626;
    }
  }

  .annotation-box {
    background: #f0f5ff;
    border-left: 4px solid #1890ff;
    padding: 12px 16px;
    border-radius: 0 8px 8px 0;
    margin: 12px 0;
    font-size: 13px;
    color: #595959;
    line-height: 1.6;

    strong {
      color: #1890ff;
    }
  }

  .sub-text,
  .hint {
    font-size: 12px;
    color: #999;
  }

  .discount-text {
    color: #1890ff;
    font-weight: 600;
  }

  .price-text {
    color: #ff4d4f;
    font-weight: 600;
  }

  .form-grid {
    display: flex;
    gap: 12px;

    .el-form-item {
      flex: 1;
    }
  }

  .cost-editor {
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    overflow: hidden;
  }

  .range-inputs {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    /deep/ .el-input-number {
      width: 90px;
    }
  }

  .cost-footer {
    padding: 8px 12px;
  }

  .switch-tip {
    margin-left: 12px;
    font-size: 12px;
    color: #999;
  }

  .preview-box {
    background: #f0f5ff;
    border-radius: 8px;
    padding: 14px;
    margin-top: 8px;
  }

  .preview-title {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 6px;
  }

  .preview-content {
    font-size: 12px;
    color: #555;
    line-height: 1.8;
  }
}
</style>
